import { Link } from 'react-router-dom';
import { useDomains, useZones } from '@/lib/queries';
import { ChevronRight } from 'lucide-react';
import type { MaintenanceLog } from '@/lib/types';

/**
 * Card-list of maintenance logs. Always card-shaped; tables don't
 * fit a phone. The dashboard, the global Logs page, and the
 * per-entity log lists all use this same component.
 */
export default function LogList({
  logs,
  emptyMessage = 'No logs yet.',
}: {
  logs: MaintenanceLog[];
  emptyMessage?: string;
}) {
  const domains = useDomains();
  const zones = useZones();

  const domainName = (id: string) =>
    (domains.data ?? []).find((d) => d.id === id)?.name ?? id;
  const zoneName = (id: string | null) =>
    id ? (zones.data ?? []).find((z) => z.id === id)?.name ?? id : null;

  if (logs.length === 0) {
    return <p className="text-sm text-muted-foreground">{emptyMessage}</p>;
  }

  return (
    <ul className="divide-y divide-rule rounded-md border border-rule bg-card" data-testid="log-list">
      {logs.map((l) => (
        <li key={l.id}>
          <Link
            to={`/logs/${l.id}`}
            data-testid={`log-row-${l.id}`}
            className="flex items-start justify-between gap-3 px-4 py-4 hover:bg-accent/40 transition-colors"
          >
            <div className="min-w-0 space-y-1">
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-tabular">
                {formatPerformed(l.performedAt)}
              </p>
              <p className="font-display text-base font-medium leading-tight">{l.title}</p>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                <span className="font-mono uppercase tracking-wider">
                  {domainName(l.domainId)}
                </span>
                {zoneName(l.zoneId) ? (
                  <span className="font-mono uppercase tracking-wider">
                    {zoneName(l.zoneId)}
                  </span>
                ) : null}
                {l.cost != null ? (
                  <span className="font-mono tabular-nums">
                    ${(l.cost / 100).toFixed(2)}
                  </span>
                ) : null}
              </div>
              {l.notes ? (
                <p className="text-sm text-foreground/75 line-clamp-2 pt-1">{l.notes}</p>
              ) : null}
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 mt-1" aria-hidden />
          </Link>
        </li>
      ))}
    </ul>
  );
}

function formatPerformed(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}
